/**
 * search-model-expand-core.js
 *
 * Shopify storefront search only matches a query term at the START of a
 * word-token. Hyundai model numbers are stored as a single token carrying a
 * brand prefix — "HY5508X", "HY3150C", "DHY8000SELR". So when a shopper types
 * the bare model ("5508", "3150C", "8000SELR") their term sits mid-token and
 * matches nothing.
 *
 * expandSearchQuery() rewrites a bare model number into an OR-union that also
 * tries each brand prefix:
 *
 *   "5508"  ->  "5508 OR HY5508 OR DHY5508 OR P5508 OR IC5508"
 *
 * "HY5508" is a prefix of the stored token "HY5508X", so it matches; the other
 * clauses simply find nothing. Because it is a union it can only ADD matches,
 * never remove the shopper's original results. Non-model queries (plain words,
 * multi-word phrases) are returned untouched.
 *
 * Pure logic, no DOM — shared by search.js (predictive dropdown) and
 * search-fallback.js (results page), and unit-tested in
 * test/search-model-expand-core.test.js.
 */
(function (global) {
  'use strict';

  // Brand prefixes shoppers routinely drop from Hyundai model numbers.
  // Order is irrelevant (the clauses are OR-ed). Add new prefixes here.
  var MODEL_PREFIXES = ['HY', 'DHY', 'P', 'IC'];

  // A "model-like" query is a single compact token — letters/digits with an
  // optional "-" or "/" — that contains at least one digit. The strict charset
  // guarantees the OR-string we build never contains a space (other than the
  // ones we add), quote, colon or parenthesis, so it can't corrupt Shopify's
  // search grammar. Anything with whitespace (a phrase) is left alone.
  var MODEL_LIKE = /^[A-Za-z0-9][A-Za-z0-9/-]*$/;

  function expandSearchQuery(raw) {
    var term = (raw == null ? '' : String(raw)).trim();

    if (!term || !MODEL_LIKE.test(term) || !/[0-9]/.test(term)) {
      return { display: term, query: term, expanded: false };
    }

    var upper = term.toUpperCase();
    var variants = [term];
    for (var i = 0; i < MODEL_PREFIXES.length; i++) {
      // Skip a prefix the shopper already typed (avoids "HYHY5508").
      if (upper.indexOf(MODEL_PREFIXES[i]) === 0) continue;
      variants.push(MODEL_PREFIXES[i] + term);
    }

    // Nothing to add (term already led with every applicable prefix) -> no-op.
    if (variants.length === 1) {
      return { display: term, query: term, expanded: false };
    }

    return { display: term, query: variants.join(' OR '), expanded: true };
  }

  var api = {
    MODEL_PREFIXES: MODEL_PREFIXES,
    expandSearchQuery: expandSearchQuery,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (global) {
    global.SearchModelExpandCore = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : this);
